import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/** 현장 › 온열질환 — 구역 및 환경 설정(V575 · 백엔드 SafeHeatController) · 체감온도 계산. */

export type HeatZone = {
  id: string;
  name: string;
  outdoor: boolean;
  hasShade: boolean;
  hasRestArea: boolean;
  waterSpot: string | null;
  cooling: string | null;
  note: string | null;
  active: boolean;
  sortOrder: number;
};

export type HeatZoneInput = Omit<HeatZone, 'id' | 'sortOrder'>;

const zonesKey = (siteId: string) => ['safe-site', siteId, 'heat-zones'] as const;

export function useHeatZones(siteId: string) {
  return useQuery({
    queryKey: zonesKey(siteId),
    queryFn: () => apiFetch<HeatZone[]>(`/v1/safe/sites/${siteId}/heat-zones`),
  });
}

/** 새로 쓰기(id 없음) · 고치기 · 지우기(body null). */
export function useHeatZoneWrite(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string | null; body: HeatZoneInput | null }): Promise<HeatZone | void> => {
      const base = `/v1/safe/sites/${siteId}/heat-zones`;
      if (body === null) return apiFetch<void>(`${base}/${id}`, { method: 'DELETE' });
      if (id) return apiFetch<HeatZone>(`${base}/${id}`, { method: 'PUT', body });
      return apiFetch<HeatZone>(base, { method: 'POST', body });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: zonesKey(siteId) }),
  });
}

/**
 * 여름철 체감온도(기상청) — 습구온도는 Stull(2011) 식.
 * Tw = T·atan[0.151977(RH+8.313659)^½] + atan(T+RH) − atan(RH−1.67633) + 0.00391838·RH^1.5·atan(0.023101·RH) − 4.686035
 * 체감 = −0.2442 + 0.55399·Tw + 0.45535·T − 0.0022·Tw² + 0.00278·Tw·T + 3.0
 */
export function feelsLikeTemp(temp: number, humidity: number): number {
  const t = temp;
  const rh = humidity;
  const tw =
    t * Math.atan(0.151977 * Math.sqrt(rh + 8.313659)) +
    Math.atan(t + rh) -
    Math.atan(rh - 1.67633) +
    0.00391838 * Math.pow(rh, 1.5) * Math.atan(0.023101 * rh) -
    4.686035;
  const value = -0.2442 + 0.55399 * tw + 0.45535 * t - 0.0022 * tw * tw + 0.00278 * tw * t + 3.0;
  return Math.round(value * 10) / 10;
}

export type HeatStage = '관심' | '주의' | '경고' | '위험';

/** 고용노동부 폭염 단계 — 체감온도 31℃ 관심 · 33℃ 주의 · 35℃ 경고 · 38℃ 위험. 31 아래는 null. */
export function heatStage(feelsLike: number): HeatStage | null {
  if (feelsLike >= 38) return '위험';
  if (feelsLike >= 35) return '경고';
  if (feelsLike >= 33) return '주의';
  if (feelsLike >= 31) return '관심';
  return null;
}

export const STAGE_BADGE: Record<HeatStage, string> = {
  관심: 'badge-blue',
  주의: 'badge-yellow',
  경고: 'badge-red',
  위험: 'badge-red',
};

/** 폭염 대응 기간의 시작(5월 15일). 그 전이면 지난해 기간. */
export function heatSeasonStart(day: string): string {
  const year = Number(day.slice(0, 4));
  const start = `${year}-05-15`;
  return day >= start ? start : `${year - 1}-05-15`;
}
